import React from 'react';
import { Select } from 'components/Select/Select';
import './FormField.scss';

/**
 * FormField Component - Generic reusable form field
 *
 * A flexible form field component that handles both view and edit modes
 * with support for various input types and custom renderers.
 *
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string|number} props.value - Field value
 * @param {boolean} props.isEditing - Whether in edit mode (default: true)
 * @param {Function} props.onChange - Change handler (value) => void
 * @param {string} props.type - Input type: text, email, url, number, select, textarea (default: 'text')
 * @param {string} props.placeholder - Input placeholder
 * @param {Array} props.options - Options for select input [{value, label}]
 * @param {Object} props.inputProps - Additional props passed to input element
 * @param {Function} props.renderView - Custom render for view mode: (value) => ReactNode
 * @param {Function} props.renderEdit - Custom render for edit mode: (value, onChange) => ReactNode
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.required - Whether field is required (default: false)
 * @param {string} props.error - Error message to display
 * @param {string} props.helperText - Helper text below the field
 * @param {Function} props.onBlur - Blur handler for validation
 *
 * @example
 * // Basic text input
 * <FormField
 *   label="Name"
 *   value={name}
 *   onChange={setName}
 * />
 *
 * @example
 * // Select dropdown
 * <FormField
 *   label="Country"
 *   type="select"
 *   value={country}
 *   onChange={setCountry}
 *   options={[{value: 'us', label: 'USA'}, {value: 'uk', label: 'UK'}]}
 * />
 *
 * @example
 * // View/Edit mode toggle
 * <FormField
 *   label="Email"
 *   value={email}
 *   isEditing={isEditing}
 *   onChange={setEmail}
 *   type="email"
 * />
 *
 * @example
 * // Custom view renderer
 * <FormField
 *   label="Website"
 *   value={website}
 *   isEditing={false}
 *   renderView={(value) => <a href={value}>{value}</a>}
 * />
 */
export const FormField = ({
  label,
  value,
  isEditing = true,
  onChange,
  onBlur,
  type = 'text',
  placeholder = '',
  options = [],
  inputProps = {},
  renderView = null,
  renderEdit = null,
  className = '',
  required = false,
  error = '',
  helperText = '',
}) => {
  /**
   * Render edit mode input
   */
  const renderEditInput = () => {
    // Custom render function provided
    if (renderEdit) {
      return renderEdit(value, onChange);
    }

    // Select input - Use custom Select component
    if (type === 'select') {
      return (
        <Select
          options={options}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder || 'Select an option'}
          required={required}
          error={error}
          {...inputProps}
        />
      );
    }

    // Textarea input
    if (type === 'textarea') {
      return (
        <textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          className={error ? 'error' : ''}
          {...inputProps}
        />
      );
    }

    // Standard input (text, email, url, number, etc.)
    return (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        className={error ? 'error' : ''}
        {...inputProps}
      />
    );
  };

  /**
   * Render view mode display
   */
  const renderViewDisplay = () => {
    // Custom render function provided
    if (renderView) {
      return renderView(value);
    }

    // Default display
    return <span className='field-value'>{value || 'Not provided'}</span>;
  };

  return (
    <div
      className={`form-field ${isEditing ? 'editing' : 'viewing'} ${
        error ? 'has-error' : ''
      } ${className}`}
    >
      {label && (
        <label className='field-label'>
          {label}
          {required && isEditing && <span className='required'>*</span>}
        </label>
      )}
      <div className='field-content'>
        {isEditing ? renderEditInput() : renderViewDisplay()}
      </div>
      {error && <span className='field-error'>{error}</span>}
      {helperText && !error && (
        <span className='field-helper'>{helperText}</span>
      )}
    </div>
  );
};
